import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
// import Typography from '@mui/material/Typography';
import Logo from 'logos/Logo';
import { error404, error500 } from 'images';
import { Logout } from 'services/user';
import { useMsal } from '@azure/msal-react';

const PREFIX = 'ErrorPage';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  red: `${PREFIX}-red`,
  green: `${PREFIX}-green`,
  appBar: `${PREFIX}-appBar`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  code: `${PREFIX}-code`,
  message: `${PREFIX}-message`,
  image: `${PREFIX}-image`,
  button: `${PREFIX}-button`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    minWidth: 900,
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    height: 200,
    '& > div': {
      margin: 'auto',
    },
  },

  [`& .${classes.red}`]: {
    backgroundColor: red,
  },

  [`& .${classes.green}`]: {
    backgroundColor: green,
  },

  [`& .${classes.appBar}`]: {
    height: 200,
    zIndex: theme.zIndex.drawer + 1,
  },

  [`& .${classes.appBarSpacer}`]: {
    minHeight: 200,
  },

  [`& .${classes.contentContainer}`]: {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    padding: 30,
    width: '100%',
    background:
      'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(220,233,245,1) 80%, rgba(152,187,223,1) 100%)',
    flex: 1,
    textAlign: 'center',
  },

  [`& .${classes.code}`]: {
    fontSize: 50,
    margin: 10,
    color: '#3544d0',
    lineHeight: 1,
  },

  [`& .${classes.message}`]: {
    fontSize: 20,
    marginTop: 0,
    color: '#3544d0',
    marginBottom: 20,
  },

  [`& .${classes.image}`]: {
    margin: 20,
  },

  [`& .${classes.button}`]: {
    padding: '5px 15px',
    borderRadius: 24,
    height: 48,
    marginRight: 15,
    fontSize: 20,
    width: 180,
  },
}));

const red = '#FF75A8';
const green = '#26C9A8';

interface Props {
  code: number;
}

export default function ErrorPage(props: Props): JSX.Element {
  const { instance } = useMsal();
  const { code } = props;
  const message = code === 500 ? 'Something went wrong' : 'Page not found';
  const imageSrc = code === 500 ? error500 : error404;

  const handleLogout = () => {
    Logout(instance);
  };

  return (
    <Root className={classes.root}>
      <CssBaseline />
      <AppBar position="absolute" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div>
            <Logo color="white" height={60} />
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.contentContainer}>
        <div className={classes.appBarSpacer} />
        <div className={classes.content}>
          <h1 className={classes.code}>{code}</h1>
          <div className={classes.message}>{message}</div>
          <div className={classes.image}>
            <img src={imageSrc} />
          </div>
          <Button variant="contained" className={classes.button} color="primary" href="/">
            Home
          </Button>
          {code === 401 && (
            <Button
              variant="contained"
              className={classes.button}
              color="primary"
              onClick={handleLogout}
            >
              Re-Login
            </Button>
          )}
        </div>
      </main>
    </Root>
  );
}
